import React from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { X, Printer, QrCode, Globe } from 'lucide-react';

export const QRCodeModal = ({ form, onClose, customUrl = null, title = "Scan to Sign Form" }) => {
    // Generate the static production URL
    // If a custom URL is provided (e.g. for studio landing), use it.
    // Otherwise fallback to the standard form link.
    const staticUrl = customUrl || `https://consent.bookink.uk/?formId=${form.id}`;

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-900/60 backdrop-blur-md animate-fade-in print:bg-white print:backdrop-blur-none flex items-center justify-center p-4 sm:p-6">
            <div className="relative w-full max-w-sm sm:max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all print:shadow-none print:w-full print:max-w-none">
                
                {/* HEADER - Hidden in Print */}
                <div className="p-6 pb-0 flex justify-between items-start print:hidden">
                    <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
                        <QrCode size={24} />
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full p-2 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 sm:p-8 pt-4 text-center">
                    <h3 className="text-2xl font-black text-slate-800 mb-2 tracking-tight print:text-4xl print:mb-8">
                        {title}
                    </h3>
                    
                    <p className="text-sm text-slate-500 mb-8 px-4 leading-relaxed print:text-xl print:text-slate-900">
                        {customUrl ? (
                            "Scan this code to visit the Studio's specialized landing page and digital form repository."
                        ) : (
                            <>Scan with your mobile device to sign<br /><strong className="text-indigo-600">{form?.title || 'this form'}</strong></>
                        )}
                    </p>

                    {/* QR CANVAS CONTAINER */}
                    <div className="flex justify-center mb-8">
                        <div className="p-6 bg-white border-2 border-slate-100 rounded-[2rem] shadow-xl shadow-indigo-100/50 print:border-4 print:border-black print:shadow-none transition-transform hover:scale-105 duration-300">
                            <QRCodeCanvas 
                                value={staticUrl} 
                                size={220} 
                                className="w-full h-auto max-w-[220px]" 
                                level="H"
                                includeMargin={false}
                            />
                        </div>
                    </div>

                    {/* URL DISPLAY */}
                    <div className="flex items-center gap-2 justify-center text-[10px] sm:text-xs text-slate-400 font-mono bg-slate-50 p-3 rounded-xl border border-slate-100 break-all mb-8 transition-all hover:bg-slate-100 hover:text-slate-600">
                        <Globe size={12} className="shrink-0" />
                        {staticUrl}
                    </div>

                    {/* ACTIONS - Hidden in Print */}
                    <div className="flex flex-col sm:flex-row gap-3 print:hidden">
                        <button
                            onClick={handlePrint}
                            className="flex-1 bg-slate-800 text-white py-3.5 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-900 transition shadow-lg flex items-center justify-center gap-2 active:scale-95"
                        >
                            <Printer size={16} /> Print QR Page
                        </button>
                        <button
                            onClick={onClose}
                            className="sm:w-24 bg-slate-100 text-slate-500 py-3.5 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-200 transition active:scale-95"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* PRINT FOOTER - Only visible in print */}
                <div className="hidden print:block text-center mt-12 border-t-2 border-slate-100 pt-8">
                    <p className="text-2xl font-bold text-slate-800">Powered by BookINK</p>
                    <p className="text-sm text-slate-500 mt-2">Professional Digital Consent Management</p>
                </div>
            </div>
        </div>
    );
};
